import React, { useState, useRef, useMemo, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Raycaster, Vector3, Vector2, Plane, BufferGeometry, Float32BufferAttribute, MeshDepthMaterial } from "three";

import { useDarkMode } from "./DarkModeSwitch";

const raycaster = new Raycaster();
// Plane in front of the camera for mouse intersection
const plane = new Plane(new Vector3(0, 0, 1), 0);

function SwarmOfDots3D({ mouse }) {
    const groupRef = useRef();
    const geometryRef = useRef();
    const dotRefs = useRef([]);
  
    const [isDarkMode] = useDarkMode();
    const { camera } = useThree();
  
    // Create 12 dots with random spherical coords
    const dots = useMemo(() => {
      const count = 3;
      return Array.from({ length: count }).map(() => {
        // radius ~3-5
        const r = 0.5 + Math.random() * 2;
        // phi ∈ [0, π], theta ∈ [0, 2π]
        const phi = Math.random() * Math.PI;
        const theta = Math.random() * 2 * Math.PI;
        // speeds
        const phiSpeed = 0.01 + Math.random() * 0.01;
        const thetaSpeed = 0.01 + Math.random() * 0.01;
  
        return { r, phi, theta, phiSpeed, thetaSpeed };
      });
    }, []);
  
    // Store line positions: need "dots.length + 1" to close the loop
    const positionsArray = useRef(new Float32Array(dots.length * 3 + 3));
    const intersection = useRef(new Vector3());
  
    useFrame(() => {
      // 1) Find the mouse intersection with the plane in front of camera
      raycaster.setFromCamera(new Vector2(mouse.x, mouse.y), camera);
      raycaster.ray.intersectPlane(plane, intersection.current);
  
      // 2) Move the entire swarm group toward that intersection
      if (groupRef.current) {
        groupRef.current.position.lerp(intersection.current, 0.025);
      }
  
      // 3) Update each dot’s angle & position in 3D
      dots.forEach((dot, i) => {
        dot.phi += dot.phiSpeed;
        dot.theta += dot.thetaSpeed;
  
        // Convert spherical -> Cartesian
        const x = dot.r * Math.sin(dot.phi) * Math.cos(dot.theta);
        const y = dot.r * Math.sin(dot.phi) * Math.sin(dot.theta);
        const z = dot.r * Math.cos(dot.phi);
  
        // Move the dot <mesh>
        if (dotRefs.current[i]) {
          dotRefs.current[i].position.set(x, y, z);
        }
  
        // Update line geometry data
        positionsArray.current[i * 3 + 0] = x;
        positionsArray.current[i * 3 + 1] = y;
        positionsArray.current[i * 3 + 2] = z;
      });
  
      // Close the loop: last vertex = first vertex
      positionsArray.current[dots.length * 3 + 0] = positionsArray.current[0];
      positionsArray.current[dots.length * 3 + 1] = positionsArray.current[1];
      positionsArray.current[dots.length * 3 + 2] = positionsArray.current[2];
  
      // 4) Update the BufferGeometry for the line
      if (geometryRef.current) {
        geometryRef.current.setAttribute(
          "position",
          new Float32BufferAttribute(positionsArray.current, 3)
        );
        geometryRef.current.computeBoundingSphere();
      }
    });
  
    return (
      <group ref={groupRef}>
        {/* Dots */}
        {dots.map((_, i) => (
          <mesh
            key={i}
            ref={(el) => (dotRefs.current[i] = el)}
            position={[0, 0, 0]} // actual orbit set in useFrame
          >
            <sphereGeometry args={[0.1, 8, 8]} />
            <meshBasicMaterial color={isDarkMode ? "white" : "black"} wireframe />
          </mesh>
        ))}
      </group>
    );
  }

// --- FloatingCubes ---
const generateRandomCubes = (count, range) => {
    return Array.from({ length: count }).map(() => ({
        position: [Math.random() * range - range / 2, Math.random() * range - range / 2, 0],
        velocity: [(Math.random() - 0.5) * 0.02, (Math.random() - 0.5) * 0.02, 0],
        size: Array.from({ length: 3 }).fill(Math.random() * 2),
    }));
};

const FloatingCubes = ({ count, range }) => {
    const cubes = useMemo(() => generateRandomCubes(count, range), [count, range]);
    const cubeRefs = useRef([]);
    const [isDarkMode] = useDarkMode();

    useFrame(() => {
        // Update each cube's position
        cubeRefs.current.forEach((cube, i) => {
            if (cube) {
                const { velocity } = cubes[i];
                cube.position.x += velocity[0];
                cube.position.y += velocity[1];
            }
        });
    });

    return (
        <group>
            {cubes.map((cube, i) => (
                <mesh key={i} ref={(el) => (cubeRefs.current[i] = el)} position={cube.position}>
                    <boxGeometry args={cube.size} />
                    <meshDepthMaterial depthTest depthWrite />
                </mesh>
            ))}
        </group>
    );
};

// --- Main AsteroidsGame ---
const AsteroidsGame = ({ style = {} }) => {
    const [mouse, setMouse] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const handleMouseMove = (e) => {
            setMouse({
                x: (e.clientX / window.innerWidth) * 2 - 1,
                y: -(e.clientY / window.innerHeight) * 2 + 1,
            });
        };
        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    return (
        <Canvas
            camera={{ position: [0, 0, 50], fov: 50, near: 45, far: 55 }}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100vw",
                height: "100vh",
                pointerEvents: "none",
                ...style,
            }}
        >
            <ambientLight color={"blue"}/>
            <SwarmOfDots3D mouse={mouse} />
            <FloatingCubes count={20} range={50} />
        </Canvas>
    );
};

export default AsteroidsGame;
